/**
 * Guía y vista previa compartidas por reglas y campañas puntuales.
 * La vista usa el mismo HTML que el envío.
 */

import { EMAIL_DATA_BLOCKS } from './commCatalog';

const FIELDS = [
    { token: '{nombre}', help: 'Nombre del perfil. Si no tiene, sale «pescador».' },
    { token: '{capturas}', help: 'Capturas que ha publicado.' },
    { token: '{capturas_zona}', help: 'Capturas en su zona principal.' },
    { token: '{capturas_restantes}', help: 'Las que le faltan para el umbral de análisis (8).' },
    { token: '{capturas_restantes_txt}', help: '«1 captura» / «3 capturas».' },
    { token: '{umbral}', help: 'Umbral de análisis listo (hoy 8).' },
    { token: '{saldo}', help: 'Importe con € («1,00 €»).' },
    { token: '{nivel}', help: 'Principiante, intermedio o experto. Si no tiene, «pescador».' },
    { token: '{pasos}', help: 'Lista de pasos pendientes («foto, ubicación») o «ninguno».' },
    { token: '{especies}', help: 'Número de especies distintas capturadas.' },
    { token: '{especie_top}', help: 'Nombre común en minúscula (p. ej. calamar).' },
    { token: '{tesoros}', help: 'Tesoros / cofres que ha abierto.' },
    { token: '{xp}', help: 'XP con separador de miles («45.922»).' },
    { token: '{dias_pro_usados}', help: 'Días desde current_period_start de su alta Pro/prueba actual.' },
    { token: '{dias_restantes}', help: 'Días que le quedan de prueba (vacío si no hay trial).' },
    { token: '{mes_nombre}', help: 'Nombre del mes calendario anterior (Madrid).' },
    { token: '{capturas_mes}', help: 'Capturas del mes anterior.' },
    { token: '{capturas_mes_txt}', help: '«1 captura» / «20 capturas».' },
    { token: '{especies_mes}', help: 'Especies distintas del mes anterior.' },
    { token: '{especies_mes_txt}', help: '«1 especie» / «4 especies».' },
    { token: '{especie_top_mes}', help: 'Especie más capturada del mes (nombre común).' },
    { token: '{capturas_especie_top_mes}', help: 'Cuántas de esa especie en el mes.' },
    { token: '{mejor_franja}', help: 'Mejor franja 4 h como Patrones («de 8 a 12 h»).' },
    { token: '{mejor_mes}', help: 'Mes del año con más capturas (histórico).' },
    { token: '{capturas_mejor_mes}', help: 'Capturas en ese mejor mes.' },
    { token: '{especies_zona}', help: 'Top especies de la comunidad a 50 km.' },
    { token: '{radio}', help: 'Siempre «50 km» (Intel/zona).' },
    { token: '{desde}', help: 'Inicio del mes anterior (etiqueta).' },
    { token: '{hasta}', help: 'Fin del mes anterior (etiqueta).' },
    { token: '{proximo_buen_dia}', help: 'Etiqueta del próximo buen día (si Pro/trial).' },
] as const;

export function CopyGuide(props: { channel: 'email' | 'push' }) {
    const isPush = props.channel === 'push';
    return (
        <div className="space-y-2 rounded-lg border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
            <p className="font-medium text-foreground">Qué puedes escribir</p>
            <ul className="space-y-1">
                {FIELDS.map((field) => (
                    <li key={field.token}>
                        <code className="text-foreground">{field.token}</code> {field.help}
                    </li>
                ))}
            </ul>
            {!isPush && (
                <div className="space-y-1 border-t border-border pt-2">
                    <p className="font-medium text-foreground">Bloques de datos (línea sola)</p>
                    <p>
                        Si faltan datos, por defecto se omiten. En plantilla puedes mapear
                        omit / fallback / skip_send por bloque (<code className="text-foreground">block_missing_policy</code>).
                    </p>
                    <ul className="space-y-1">
                        {EMAIL_DATA_BLOCKS.map((b) => (
                            <li key={b.token}>
                                <code className="text-foreground">{b.token}</code> {b.help}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {isPush ? (
                <p>Un aviso no lleva botones. Al tocarlo se abre el destino que pongas debajo.</p>
            ) : (
                <div className="space-y-1">
                    <p>El botón principal es el campo Botón más su enlace. En el texto también vale una línea sola:</p>
                    <p><code className="text-foreground">[Abrir AiPeces]</code> usa ese enlace.</p>
                    <p><code className="text-foreground">[Ver la tienda](/tienda)</code> es un segundo botón, con su propia ruta. La ruta puede ser interna (<code className="text-foreground">/tienda</code>) o una https.</p>
                </div>
            )}
            <p>
                La vista previa local usa datos de ejemplo (Ana). Usa «Usuario real» para renderizar
                con capturas y zona de alguien concreto.
            </p>
        </div>
    );
}

export function MessagePreview(props: {
    channel: 'email' | 'push';
    subject: string;
    bodyText: string;
    html: string;
    missing: string[];
    clickUrl: string;
}) {
    const isPush = props.channel === 'push';
    const pushText = props.bodyText
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line && !/^\[[^\]\n]+\](\([^)]+\))?$/.test(line))
        .join('\n')
        .trim();

    return (
        <div className="flex min-h-0 min-w-0 flex-col">
            <p className="mb-2 text-sm font-medium">{isPush ? 'Así llegará el aviso' : 'Así lo verá'}</p>
            {props.missing.length > 0 && (
                <p className="mb-2 text-sm text-amber-400">Sin valor: {props.missing.join(', ')}</p>
            )}
            {isPush ? (
                <div className="rounded-2xl border border-slate-700 bg-slate-900 p-4 text-white">
                    <p className="text-[11px] uppercase tracking-wide text-slate-400">AiPeces</p>
                    <p className="mt-1 text-base font-semibold">{props.subject || 'Sin título'}</p>
                    <p className="mt-2 whitespace-pre-wrap text-sm text-slate-100">{pushText || 'Sin texto'}</p>
                    {props.clickUrl.trim() && (
                        <p className="mt-3 text-[11px] text-slate-400">Al tocarlo abre {props.clickUrl.trim()}</p>
                    )}
                </div>
            ) : (
                <div
                    className="flex min-h-[420px] flex-1 flex-col overflow-hidden rounded-lg border border-slate-200"
                    style={{ colorScheme: 'light', background: '#e2e8f0' }}
                >
                    <div className="shrink-0 border-b border-slate-200 bg-white px-4 py-2.5">
                        <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
                            Asunto
                        </p>
                        <p className="text-sm font-semibold text-slate-900">{props.subject}</p>
                    </div>
                    <div className="relative min-h-0 flex-1">
                        <iframe
                            title="Vista previa del correo"
                            srcDoc={props.html}
                            sandbox=""
                            referrerPolicy="no-referrer"
                            className="absolute inset-0 h-full w-full border-0 bg-[#f8fafc]"
                            style={{ colorScheme: 'light' }}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}
